import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Play, CheckCircle, Globe, Shield, Clock, Euro, 
  Video, Phone, FileText, Search, Calendar, Languages,
  Stethoscope, CreditCard, FileCheck, ArrowRight, User, UserCog
} from 'lucide-react';

// ==================== PATIENT TUTORIAL PAGE ====================
export function PatientTutorial() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: "1. Choisissez votre spécialité",
      icon: <Stethoscope className="h-6 w-6" />,
      description: "Sélectionnez le type de médecin dont vous avez besoin",
      details: [
        "Dermatologie pour un problème de peau",
        "Cardiologie pour le cœur",
        "Psychiatrie pour le moral",
        "Et 13 autres spécialités...",
      ],
      tip: "⚠️ Un dermatologue consulte UNIQUEMENT pour la dermatologie, pas pour l'ORL !"
    },
    {
      title: "2. Sélectionnez vos langues",
      icon: <Languages className="h-6 w-6" />,
      description: "Indiquez toutes les langues que vous parlez",
      details: [
        "Cliquez sur Français ET English si vous parlez les deux",
        "La plateforme cherche des médecins parlant L'UNE de ces langues",
        "Vous ne verrez QUE des médecins avec qui vous pouvez communiquer",
      ],
      tip: "💡 Plus vous sélectionnez de langues, plus vous avez de choix !"
    },
    {
      title: "3. Trouvez votre médecin",
      icon: <Search className="h-6 w-6" />,
      description: "Parcourez les médecins vérifiés disponibles",
      details: [
        "Tous les médecins sont inscrits à l'Ordre (RPPS vérifié)",
        "Voyez leur pays, langues parlées et tarifs",
        "Badge 'Vérifié' = médecin authentique",
      ],
      tip: "🌍 Consultez un médecin belge, suisse ou allemand depuis la France !"
    },
    {
      title: "4. Réservez et payez",
      icon: <CreditCard className="h-6 w-6" />,
      description: "Choisissez un créneau et payez en ligne",
      details: [
        "Sélectionnez une date et heure disponible",
        "Paiement sécurisé par carte bancaire",
        "Confirmation immédiate par email",
      ],
      tip: "💰 Prix affiché = prix final (frais de service inclus)"
    },
    {
      title: "5. Consultez en vidéo",
      icon: <Video className="h-6 w-6" />,
      description: "Rejoignez la consultation le jour J",
      details: [
        "Connectez-vous 5 minutes avant",
        "Testez votre caméra et microphone",
        "La consultation dure environ 15-20 minutes",
      ],
      tip: "📱 Fonctionne sur ordinateur, tablette et smartphone !"
    },
    {
      title: "6. Recevez votre ordonnance",
      icon: <FileCheck className="h-6 w-6" />,
      description: "Ordonnance envoyée par email après la consultation",
      details: [
        "Ordonnance valide dans toute l'Europe (Directive 2011/24/UE)",
        "Téléchargeable en PDF",
        "Facture disponible dans votre espace",
      ],
      tip: "🇪🇺 Une ordonnance d'un médecin belge est valable en France !"
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-blue-100 text-blue-800 hover:bg-blue-100">
          <User className="h-3 w-3 mr-1" /> Guide Patient
        </Badge>
        <h1 className="text-3xl font-bold mb-2">Comment consulter un médecin en ligne ?</h1>
        <p className="text-muted-foreground">
          Trouvez un médecin vérifié disponible en quelques minutes
        </p>
      </div>

      {/* Video Embed Placeholder */}
      <Card className="mb-8 overflow-hidden">
        <div className="aspect-video bg-gradient-to-br from-blue-600 to-indigo-700 flex flex-col items-center justify-center text-white">
          <Play className="h-16 w-16 mb-4 opacity-80" />
          <p className="text-xl font-semibold">Tutoriel vidéo - 3 minutes</p>
          <p className="text-sm opacity-80 mt-2">Comment trouver et consulter un médecin sur drbooking</p>
          <Button variant="secondary" className="mt-4">
            <Play className="h-4 w-4 mr-2" /> Regarder la vidéo
          </Button>
        </div>
      </Card>

      {/* Why use drbooking */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-green-50 dark:bg-green-950 border-green-200">
          <CardContent className="pt-4 text-center">
            <Shield className="h-8 w-8 mx-auto text-green-600 mb-2" />
            <p className="font-semibold text-green-800 dark:text-green-200">Médecins Vérifiés</p>
            <p className="text-sm text-green-600 dark:text-green-400">Tous inscrits à l'Ordre</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
          <CardContent className="pt-4 text-center">
            <Clock className="h-8 w-8 mx-auto text-blue-600 mb-2" />
            <p className="font-semibold text-blue-800 dark:text-blue-200">RDV Immédiat</p>
            <p className="text-sm text-blue-600 dark:text-blue-400">Pas de délai d'attente</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200">
          <CardContent className="pt-4 text-center">
            <Globe className="h-8 w-8 mx-auto text-purple-600 mb-2" />
            <p className="font-semibold text-purple-800 dark:text-purple-200">31 Pays Européens</p>
            <p className="text-sm text-purple-600 dark:text-purple-400">Ordonnance valide partout</p>
          </CardContent>
        </Card>
      </div>

      {/* Step by Step Guide */}
      <h2 className="text-2xl font-bold mb-4">Guide étape par étape</h2>
      
      <div className="space-y-4">
        {steps.map((step, index) => (
          <Card 
            key={index}
            className={`cursor-pointer transition-all ${activeStep === index ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setActiveStep(activeStep === index ? -1 : index)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${activeStep === index ? 'bg-primary text-white' : 'bg-muted'}`}>
                  {step.icon}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{step.title}</CardTitle>
                  <CardDescription>{step.description}</CardDescription>
                </div>
                <ArrowRight className={`h-5 w-5 transition-transform ${activeStep === index ? 'rotate-90' : ''}`} />
              </div>
            </CardHeader>
            {activeStep === index && (
              <CardContent>
                <ul className="space-y-2 mb-4">
                  {step.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span className="text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-800 dark:text-yellow-200">{step.tip}</p>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* CTA */}
      <Card className="mt-8 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
        <CardContent className="pt-6 text-center">
          <h3 className="text-xl font-bold mb-2">Prêt à consulter ?</h3>
          <p className="text-muted-foreground mb-4">Trouvez un médecin disponible maintenant</p>
          <Button size="lg" onClick={() => window.location.href = '/consultations/search'}>
            <Search className="h-4 w-4 mr-2" /> Trouver un médecin
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PROFESSIONAL TUTORIAL PAGE ====================
export function ProfessionalTutorial() {
  const [activeStep, setActiveStep] = useState(0);

  const steps = [
    {
      title: "1. Vérifiez votre inscription à l'Ordre",
      icon: <Shield className="h-6 w-6" />,
      description: "Prouvez que vous êtes un médecin inscrit",
      details: [
        "France : entrez votre numéro RPPS (11 chiffres)",
        "Belgique : votre numéro INAMI",
        "Suisse : votre numéro GLN",
        "Vérification INSTANTANÉE pour la France via l'Annuaire Santé",
      ],
      tip: "🔒 Cette étape garantit la sécurité des patients et votre crédibilité."
    },
    {
      title: "2. Définissez vos tarifs",
      icon: <Euro className="h-6 w-6" />,
      description: "Fixez librement le prix de vos consultations",
      details: [
        "Consultation vidéo : ex. 50€",
        "Consultation téléphonique : ex. 40€",
        "Avis médical écrit : ex. 30€",
        "Le patient paie votre tarif + 5% de frais",
        "Vous recevez votre tarif - 10% de commission",
      ],
      tip: "💡 Exemple : Tarif 50€ → Patient paie 52,50€ → Vous recevez 45€"
    },
    {
      title: "3. Sélectionnez vos langues",
      icon: <Languages className="h-6 w-6" />,
      description: "Indiquez les langues que vous parlez",
      details: [
        "Seuls les patients parlant ces langues vous trouveront",
        "Plus de langues = plus de patients potentiels",
        "Important pour une bonne communication médicale",
      ],
      tip: "🌍 Parler anglais vous ouvre à des millions de patients européens !"
    },
    {
      title: "4. Définissez vos disponibilités",
      icon: <Calendar className="h-6 w-6" />,
      description: "Configurez vos créneaux de téléconsultation",
      details: [
        "Choisissez vos jours et heures de disponibilité",
        "Définissez un nombre max de consultations/jour",
        "Bloquez des périodes si besoin",
      ],
      tip: "📅 Vous pouvez modifier vos disponibilités à tout moment."
    },
    {
      title: "5. Recevez des réservations",
      icon: <User className="h-6 w-6" />,
      description: "Les patients vous trouvent et réservent",
      details: [
        "Notification par email à chaque réservation",
        "Voyez les infos du patient et le motif",
        "Le paiement est sécurisé par Stripe",
      ],
      tip: "📧 Configurez vos notifications pour ne rien manquer."
    },
    {
      title: "6. Consultez et facturez",
      icon: <Video className="h-6 w-6" />,
      description: "Réalisez la consultation et envoyez l'ordonnance",
      details: [
        "Lancez la vidéo d'un clic",
        "Rédigez l'ordonnance dans l'application",
        "La facture est générée automatiquement",
        "Le paiement arrive sur votre compte Stripe",
      ],
      tip: "💰 Vos revenus sont virés automatiquement chaque semaine."
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="text-center mb-8">
        <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
          <UserCog className="h-3 w-3 mr-1" /> Guide Professionnel
        </Badge>
        <h1 className="text-3xl font-bold mb-2">Comment activer la téléconsultation ?</h1>
        <p className="text-muted-foreground">
          Élargissez votre patientèle à toute l'Europe en quelques minutes
        </p>
      </div>

      {/* Video Embed Placeholder */}
      <Card className="mb-8 overflow-hidden">
        <div className="aspect-video bg-gradient-to-br from-emerald-600 to-teal-700 flex flex-col items-center justify-center text-white">
          <Play className="h-16 w-16 mb-4 opacity-80" />
          <p className="text-xl font-semibold">Tutoriel vidéo - 3 minutes</p>
          <p className="text-sm opacity-80 mt-2">Comment configurer votre espace téléconsultation</p>
          <Button variant="secondary" className="mt-4">
            <Play className="h-4 w-4 mr-2" /> Regarder la vidéo
          </Button>
        </div>
      </Card>

      {/* Benefits */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="bg-emerald-50 dark:bg-emerald-950 border-emerald-200">
          <CardContent className="pt-4 text-center">
            <Globe className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
            <p className="font-semibold text-emerald-800 dark:text-emerald-200">450M de patients</p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">Accès à toute l'Europe</p>
          </CardContent>
        </Card>
        <Card className="bg-blue-50 dark:bg-blue-950 border-blue-200">
          <CardContent className="pt-4 text-center">
            <CreditCard className="h-8 w-8 mx-auto text-blue-600 mb-2" />
            <p className="font-semibold text-blue-800 dark:text-blue-200">Paiement sécurisé</p>
            <p className="text-sm text-blue-600 dark:text-blue-400">Via Stripe Connect</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 dark:bg-purple-950 border-purple-200">
          <CardContent className="pt-4 text-center">
            <Shield className="h-8 w-8 mx-auto text-purple-600 mb-2" />
            <p className="font-semibold text-purple-800 dark:text-purple-200">100% Légal</p>
            <p className="text-sm text-purple-600 dark:text-purple-400">Directive 2005/36/CE</p>
          </CardContent>
        </Card>
      </div>

      {/* Step by Step Guide */}
      <h2 className="text-2xl font-bold mb-4">Guide de configuration</h2>
      
      <div className="space-y-4">
        {steps.map((step, index) => (
          <Card 
            key={index}
            className={`cursor-pointer transition-all ${activeStep === index ? 'ring-2 ring-emerald-500' : ''}`}
            onClick={() => setActiveStep(activeStep === index ? -1 : index)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${activeStep === index ? 'bg-emerald-500 text-white' : 'bg-muted'}`}>
                  {step.icon}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-lg">{step.title}</CardTitle>
                  <CardDescription>{step.description}</CardDescription>
                </div>
                <ArrowRight className={`h-5 w-5 transition-transform ${activeStep === index ? 'rotate-90' : ''}`} />
              </div>
            </CardHeader>
            {activeStep === index && (
              <CardContent>
                <ul className="space-y-2 mb-4">
                  {step.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span className="text-sm">{detail}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 rounded-lg p-3">
                  <p className="text-sm text-emerald-800 dark:text-emerald-200">{step.tip}</p>
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* CTA */}
      <Card className="mt-8 bg-gradient-to-r from-emerald-500/10 to-teal-500/5 border-emerald-200">
        <CardContent className="pt-6 text-center">
          <h3 className="text-xl font-bold mb-2">Prêt à commencer ?</h3>
          <p className="text-muted-foreground mb-4">Configurez votre espace téléconsultation maintenant</p>
          <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => window.location.href = '/consultations/settings'}>
            <UserCog className="h-4 w-4 mr-2" /> Configurer mes paramètres
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default { PatientTutorial, ProfessionalTutorial };
